import { computed } from 'vue';
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router';

const WIDGET_BACK_LABEL = 'Back';

type WidgetBackLocation = {
  target: string;
  label: string;
};

function readWidgetBackLocation(): WidgetBackLocation | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const target = window.history.state?.widgetBackTarget;
  if (typeof target !== 'string' || !target) {
    return null;
  }

  const label =
    typeof window.history.state?.widgetBackLabel === 'string' && window.history.state.widgetBackLabel
      ? window.history.state.widgetBackLabel
      : WIDGET_BACK_LABEL;

  return { target, label };
}

export function useWidgetBackNavigation() {
  const route = useRoute();
  const router = useRouter();

  const widgetBackLocation = computed(() => {
    void route.fullPath;
    return readWidgetBackLocation();
  });

  const showWidgetBack = computed(() => Boolean(widgetBackLocation.value));
  const widgetBackLabel = computed(() => widgetBackLocation.value?.label ?? WIDGET_BACK_LABEL);

  async function goBackFromWidget() {
    const target = widgetBackLocation.value?.target;
    if (!target) {
      return;
    }

    if (typeof window !== 'undefined' && typeof window.history.state?.back === 'string' && window.history.state.back) {
      router.back();
      return;
    }

    await router.push(target);
  }

  async function pushFromWidget(to: RouteLocationRaw, label = WIDGET_BACK_LABEL) {
    const state = {
      widgetBackTarget: route.fullPath,
      widgetBackLabel: label,
    };

    if (typeof to === 'string') {
      await router.push({ path: to, state });
      return;
    }

    await router.push({
      ...(to as Record<string, unknown>),
      state: {
        ...((to as { state?: Record<string, unknown> }).state ?? {}),
        ...state,
      },
    });
  }

  return {
    goBackFromWidget,
    pushFromWidget,
    showWidgetBack,
    widgetBackLabel,
  };
}
